import { useState } from "react";
import { ArrowDown01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "shared/utils";
import { Avatar } from "shared/components/Avatar";
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from "shared/components/Popover";
import { useAssigneeFilter } from "shared/components/Assignee/hooks";
import { AssigneeSearchInput } from "shared/components/Assignee/AssigneeSearchInput";
import { AssigneeUserRow } from "shared/components/Assignee/AssigneeUserRow";

const ANY_LABEL = "Any";

const StatusMessage = ({ children }: { children: string }) => (
  <div className="px-4 py-2.5 text-content-400 text-sm">{children}</div>
);

const AssigneeListBody = ({
  isLoading,
  users,
  selectedIds,
  authId,
  onToggle
}: {
  isLoading: boolean;
  users: { id: number; name: string }[];
  selectedIds: number[] | null;
  authId: number | undefined;
  onToggle: (userId: number) => void;
}) => {
  if (isLoading) return <StatusMessage>Loading users...</StatusMessage>;
  if (users.length === 0) return <StatusMessage>No users found</StatusMessage>;
  return (
    <>
      {users.map((user) => (
        <AssigneeUserRow
          key={user.id}
          userId={user.id}
          name={user.name}
          isSelected={selectedIds?.includes(user.id) ?? false}
          isCurrentUser={user.id === authId}
          onToggle={onToggle}
        />
      ))}
    </>
  );
};

const AnyOption = ({
  selected,
  onClick
}: {
  selected: boolean;
  onClick: () => void;
}) => (
  <div
    role="option"
    aria-selected={selected}
    tabIndex={0}
    className={cx(
      "flex items-center px-4 py-2.5 cursor-pointer hover:bg-alpha-40 border-b border-alpha-40",
      selected ? "bg-alpha-40" : ""
    )}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
  >
    <span className="text-base font-medium text-content-200">{ANY_LABEL}</span>
  </div>
);

export const AssigneeFilterChip = ({
  selectedIds,
  onChange
}: {
  selectedIds: number[] | null;
  onChange: (ids: number[] | null) => void;
}) => {
  const [open, setOpen] = useState(false);

  const filter = useAssigneeFilter({
    open,
    selectedUserIds: selectedIds,
    onSelectedUserIdsChange: onChange
  });

  return (
    <Popover open={open} onOpenChange={setOpen} placement="bottom-end">
      <PopoverTrigger onClick={() => setOpen(true)}>
        <button
          type="button"
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-alpha-100 text-xs whitespace-nowrap min-h-[40px] shadow-xs"
        >
          <span className="text-content-200 font-medium">Assigned to:</span>
          {filter.selectedCount > 0 ? (
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex items-center -space-x-2 shrink-0">
                {filter.visibleSelectedUsers.map((user) => (
                  <Avatar
                    key={user.id}
                    name={user.name}
                    className="text-[9px] text-content-1000 border-2 border-content-1000-inverted"
                    size={20}
                  />
                ))}
                {filter.extraSelectedCount > 0 ? (
                  <div className="size-5 rounded-full bg-white text-content-1000 text-[9px] font-bold flex items-center justify-center border-2 border-content-1000-inverted">
                    +{filter.extraSelectedCount}
                  </div>
                ) : null}
              </div>
              <span className="font-bold text-content-1000 truncate max-w-[140px]">
                {filter.displayText}
              </span>
            </div>
          ) : (
            <span className="font-bold text-content-1000">{ANY_LABEL}</span>
          )}
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={16}
            className={cx(
              "text-content-1000 transition-transform shrink-0",
              open ? "rotate-180" : ""
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[251px] p-0 rounded-lg border border-alpha-100 shadow-xl bg-content-1000-inverted z-50"
        role="listbox"
      >
        <AnyOption
          selected={selectedIds == null}
          onClick={() => onChange(null)}
        />
        <AssigneeSearchInput
          id="reporting-assignee-filter-search"
          placeholder="Search assignee"
          value={filter.search}
          onChange={filter.setSearch}
        />
        <div className="max-h-[300px] overflow-y-auto">
          <AssigneeListBody
            isLoading={filter.isLoading}
            users={filter.sortedUsers}
            selectedIds={selectedIds}
            authId={filter.auth?.id}
            onToggle={filter.handleToggleUser}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
